import { BtnLink, Duration, Form, MrtTable } from '@mi-front/ui';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import { Checkbox, FormControlLabel, FormGroup, Paper } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { Grid } from '@mui/material';
import * as yup from 'yup';
import { GipReportStatsAndNavigation } from '../../../../common/GipReportStatsAndNavigation';
import { GipAlternativeReportType } from '../../types';
import { useIncludeMatches } from './useIncludeMatches';

import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import { StationsSelect } from '@app/components/stations/StationsSelect';
import { ReportResultProps } from '@app/core/reports/types';
import { useStations, useStationsFinder } from '@app/core/stations/hooks/useStations';
import { buildLoggerStreamUrl } from '@app/modules/logger/screens/stream/helpers/build-logger-stream-url';
import { DateTimeStr, Seconds, Station } from '@mi-front/core';
import { useIntl } from 'react-intl';

export const GipAlternativeReportResultView = (p: ReportResultProps<GipAlternativeReportType>) => {
  const { includeAll, toggleIncludeAll, setRowSelection, rowSelection } = useIncludeMatches(
    p.result.matches,
  );

  const { $t } = useIntl();
  const stations = useStations();
  const finder = useStationsFinder();

  const filterStations = stations.filter(
    (s) => !p.values.stations?.length || !!p.values.stations?.find((_s) => s.id === _s.id),
  );

  const [filter, setFilter] = useState<{
    station: Station | null;
  }>({
    station: null,
  });

  const filteredMatches = p.result.matches.filter(
    (m) => !filter.station || m.stationId === filter.station.id,
  );

  const form = (
    <Form
      initialValues={{
        station: null,
      }}
      validationSchema={yup.object().shape({})}
      onSubmit={(data) => {
        // noop
      }}
      onChange={(data) => {
        setFilter({
          station: data.station,
        });
      }}
      actions={() => null}
    >
      {(p) => {
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={5}>
              <StationsSelect name={'station'} stations={filterStations} multiple={false} />
            </Grid>
          </Grid>
        );
      }}
    </Form>
  );

  const matchesContent = (
    <>
      <Paper sx={{ px: 2, mb: 2 }}>
        <div className={'pt-3 mb-3'}>
          {$t({
            id: 'audit.gipReport.resultOptions',
            defaultMessage: 'Opciones de resultados',
          })}
        </div>
        {form}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAll}
                onChange={(evt, checked) => toggleIncludeAll(checked)}
              />
            }
            label={$t({
              id: 'audit.gipReport.includeAllMatches',
              defaultMessage: 'Incluir todos los matches encontrados en el export',
            })}
          />
        </FormGroup>
      </Paper>

      <MrtTable
        data={filteredMatches}
        enableRowSelection={!includeAll}
        getRowId={(row) => row.id}
        onRowSelectionChange={setRowSelection}
        state={{ rowSelection }}
        initialState={{
          sorting: [{ id: 'date', desc: true }],
        }}
        columns={[
          {
            header: $t({
              id: 'audit.gipReport.result.date',
              defaultMessage: 'Fecha',
            }),
            size: 150,
            id: 'date',
            accessorFn: (item) => {
              return item.datetime;
            },
            Cell: ({ row }) => {
              const station = finder(row.original.stationId);

              return <LDate value={row.original.datetime as DateTimeStr} tz={station?.timezone} />;
            },
          },
          {
            header: $t({
              id: 'audit.gipReport.result.time',
              defaultMessage: 'Hora',
            }),
            id: 'time',
            size: 140,
            accessorFn: (item) => {
              const station = finder(item.stationId);

              return (
                <LTime
                  value={item.datetime as DateTimeStr}
                  second="numeric"
                  tz={station?.timezone}
                />
              );
            },
          },
          {
            id: 'station',
            header: $t({
              id: 'audit.gipReport.result.station',
              defaultMessage: 'Emisora',
            }),
            size: 200,
            accessorFn: (item) => {
              return finder(item.stationId)?.name;
            },
          },
          {
            header: $t({
              id: 'audit.gipReport.result.advertiser',
              defaultMessage: 'Anunciante',
            }),
            accessorKey: 'metadata.advertiser',
            size: 200,
          },
          {
            header: $t({
              id: 'audit.gipReport.result.campaign',
              defaultMessage: 'Campaña',
            }),
            accessorKey: 'metadata.campaign',
            size: 300,
          },
          {
            header: $t({
              id: 'audit.gipReport.result.version',
              defaultMessage: 'Versión',
            }),
            accessorKey: 'metadata.version',
            size: 300,
          },
          {
            header: $t({
              id: 'audit.props.customId',
              defaultMessage: 'Custom ID',
            }),
            accessorKey: 'customId',
            size: 190,
          },
          {
            header: $t({
              id: 'audit.gipReport.result.duration',
              defaultMessage: 'Duración',
            }),
            accessorKey: 'audio.duration',
            size: 150,
            Cell: ({ row, renderedCellValue }) => {
              return <Duration seconds={((renderedCellValue as number) / 1000) as Seconds} />;
            },
          },
          {
            header: $t({
              id: 'audit.gipReport.result.order',
              defaultMessage: 'Orden',
            }),
            size: 180,
            accessorKey: 'metadata.order',
          },
          {
            header: $t({
              id: 'common.text.actions',
              defaultMessage: 'Acciones',
            }),
            enableSorting: false,
            enableColumnFilter: false,
            accessorFn: (e) => {
              const durationMs = (e.audio?.duration ?? 0) as number;
              const fromMs = moment(e.datetime).subtract(10, 'seconds').valueOf();
              const toMs = fromMs + (durationMs > 0 ? durationMs : 120 * 1000);
              const stationSlug = finder(e.stationId)?.slug ?? e.stationId;
              const url = buildLoggerStreamUrl(stationSlug, {
                selection: { from: fromMs, to: toMs },
              });
              return (
                <BtnLink
                  size="xs"
                  to={url}
                  openInNewTab
                  icon={<OpenInNewIcon />}
                  className="mr-2"
                >
                  {$t({
                    id: 'common.show',
                    defaultMessage: 'Mostrar',
                  })}
                </BtnLink>
              );
            },
          },
        ]}
      />
    </>
  );

  const additionalChips = [
    {
      value: 'matches',
      label: `Matches (${filteredMatches.length})`,
      icon: <PlaylistPlayIcon />,
      content: matchesContent,
    },
  ];

  return (
    <GipReportStatsAndNavigation additionalChips={additionalChips} defaultActiveChip="matches" />
  );
};
